import { Mesh, ShaderMaterial, Vector2, Vector4 } from 'three';

import gsap from 'gsap';

import fragmentShader from '../../../../shaders/gallery-frag.glsl';
import vertexShader from '../../../../shaders/image-vert.glsl';

import { map } from '../../../helpers';

export default class Gallery extends Mesh {

	constructor({
		el,
		type,
		assets,
		sizes,
		controls,
		geometry,
	}) {
		super();

		this.el = el;
		this.controls = controls;

		this.type = type;
		this.assets = assets;

		this.bounds = this.assets[0].image.bounds;
		this.sizes = sizes;

		this.scroll = 0;
		this.active = 0;

		this.textures = [];

		this.isFirst = true;
		this.isPeeking = false;
		this.isMouseenter = false;

		this.timeoutMouseenter = null;

		for (const asset of this.assets) {
			this.textures.push(asset.texture);
		}

		this.el.style.backgroundColor = 'transparent';

		this.geometry = geometry;

		this.visible = false;

		this.createMaterial(this.textures);
		this.addEventListeners();
	}

	/**
	 * Listeners
	 */
	addEventListeners() {
		this.el.addEventListener('gallery:click', this.#onControlClick);
		this.el.addEventListener('gallery:mouseenter', this.#onMouseEnter);
		this.el.addEventListener('gallery:mouseleave', this.#onMouseLeave);
	}

	removeEventListeners() {
		this.el.removeEventListener('gallery:click', this.#onControlClick);
		this.el.removeEventListener('gallery:mouseenter', this.#onMouseEnter);
		this.el.removeEventListener('gallery:mouseleave', this.#onMouseLeave);
	}

	/**
	 * Events
	 */
	#onControlClick = (e) => {
		if (!this.isAnimating) {
			this.isAnimating = true;
			this.isPeeking = false;

			const direction = e.detail;

			if (direction === 1) {
				this.active++;
				this.material.uniforms.direction.value = new Vector2(-1.0, 0);

				if (this.active < this.textures.length) {
					this.material.uniforms.image.value = this.textures[this.active - 1];
				} else {
					this.active = 0;
					this.material.uniforms.image.value = this.textures[this.textures.length - 1];
				}

				this.material.uniforms.image2.value = this.textures[this.active];

			} else if (direction === 0) {
				this.active--;
				this.material.uniforms.direction.value = new Vector2(1.0, 0);

				if (this.active < 0) {
					this.active = this.textures.length - 1;
					this.material.uniforms.image.value = this.textures[0]; // eslint-disable-line
				} else {
					this.material.uniforms.image.value = this.textures[this.active + 1];
				}

				this.material.uniforms.image2.value = this.textures[this.active];
			}

			if (!this.isFirst) {
				this.material.uniforms.progressBend.value = 0;
				this.material.uniforms.progressWipe.value = 0;
			}

			this.material.uniforms.progressBw.value = 0;
			this.material.uniforms.progressMove.value = 0;

			this.timelineTransition = gsap.timeline({
				onComplete: () => {
					this.isAnimating = false;
				}
			});
			this.timelineTransition
				.to(this.material.uniforms.progressBend, {
					ease: 'power4.inOut',
					duration: this.isFirst ? 0.3 : 0.35,
					value: 1,
					repeat: 1,
					yoyo: true
				}, 'start')
				.to(this.material.uniforms.progressBw, {
					ease: 'power4.inOut',
					duration: 0.875,
					value: 1
				}, 'start')
				.to(this.material.uniforms.progressMove, {
					ease: 'power4.inOut',
					duration: 0.875,
					value: 1
				}, 'start')
				.to(this.material.uniforms.progressWipe, {
					ease: 'power4.inOut',
					duration: 1.125,
					value: 1
				}, 'start');

				this.isFirst = false;
		}
	}

	#onMouseEnter = (e) => {
		this.isMouseenter = true;

		if (this.timeoutMouseenter) {
			clearTimeout(this.timeoutMouseenter);
			this.timeoutMouseenter = null;
		}

		this.timeoutMouseenter = setTimeout(() => {
			if (this.isMouseenter) {
				if (this.timelinePeekIn) {
					this.timelinePeekIn.kill();
					this.timelinePeekIn = null;
				}

				this.isPeeking = true;

				if (this.timelineTransition) {
					this.timelineTransition.then(() => {
						this.timelineTransition.kill();
						this.timelineTransition = null;
						this.peekIn(e);
					});
				} else if (this.timelinePeekOut) {
					this.timelinePeekOut.then(() => {
						this.timelinePeekOut.kill();
						this.timelinePeekOut = null;
						this.peekIn(e);
					});
				} else {
					this.peekIn(e);
				}
			}
		}, 250);
	}

	#onMouseLeave = () => {
		this.isMouseenter = false;
		this.isFirst = true;

		if (this.isAnimating) {
			return;
		}

		if (this.isPeeking) {
			this.isPeeking = false;
			this.timelinePeekOut = gsap.timeline();
			this.timelinePeekOut
				.to(this.material.uniforms.progressBend, {
					ease: 'expo.inOut',
					duration: 0.5,
					value: 0,
				}, 'start')
				.to(this.material.uniforms.progressWipe, {
					ease: 'expo.inOut',
					duration: 0.5,
					value: 0
				}, 'start');
		}
	}

	peekIn(e) {
		const direction = e.detail;
		let current;

		if (direction === 1) {
			current = this.active + 1;
			this.material.uniforms.direction.value = new Vector2(-1.0, 0);

			if (current < this.textures.length) {
				this.material.uniforms.image.value = this.textures[current - 1];
			} else {
				current = 0;
				this.material.uniforms.image.value = this.textures[this.textures.length - 1];
			}

			this.material.uniforms.image2.value = this.textures[current];

		} else if (direction === 0) {
			current = this.active - 1;
			this.material.uniforms.direction.value = new Vector2(1.0, 0);

			if (current < 0) {
				current = this.textures.length - 1;
				this.material.uniforms.image.value = this.textures[0]; // eslint-disable-line
			} else {
				this.material.uniforms.image.value = this.textures[current + 1];
			}

			this.material.uniforms.image2.value = this.textures[current];
		}

		this.material.uniforms.progressBend.value = 0;
		this.material.uniforms.progressBw.value = 0;
		this.material.uniforms.progressMove.value = 0;
		this.material.uniforms.progressWipe.value = 0;

		this.timelinePeekIn = gsap.timeline();
		this.timelinePeekIn
			.to(this.material.uniforms.progressBend, {
				ease: 'expo.inOut',
				duration: 0.5,
				value: 0.5,
			}, 'start')
			.to(this.material.uniforms.progressWipe, {
				ease: 'expo.inOut',
				duration: 0.5,
				value: 0.025
			}, 'start');
	}

	/**
	 * Create
	 */

	createMaterial(textures) {
		const imageAspect = this.bounds.imageHeight / this.bounds.imageWidth;
		let a1;
		let a2;

		if (this.bounds.height / this.bounds.width > imageAspect) {
			a1 = (this.bounds.width / this.bounds.height) * imageAspect;
			a2 = 1;
		} else {
			a1 = 1;
			a2 = this.bounds.height / this.bounds.width / imageAspect;
		}

		this.material = new ShaderMaterial({
			depthTest: false,
			depthWrite: false,
			uniforms: {
				image: {
					value: textures[0]
				},
				image2: {
					value: textures[1]
				},
				imageResolution: {
					type: 'v4',
					value: new Vector4(this.bounds.width, this.bounds.height, a1, a2)
				},
				resolution: {
					type: 'v2',
					value: new Vector2(this.sizes.screen.width, this.sizes.screen.height)
				},
				alpha: {
					value: 0
				},
				direction: {
					type: 'v2',
					value: new Vector2(1.0, 0)
				},
				scale: {
					value: 0
				},
				smoothness: {
					value: 0
				},
				progress: {
					value: 0
				},
				progress2: {
					value: 0
				},
				progress3: {
					value: 0
				},
				progressBw: {
					value: 0
				},
				progressMove: {
					value: 0
				},
				progressBend: {
					value: 0
				},
				progressWipe: {
					value: 0
				},
				parallax: {
					value: 0
				},
				transition: {
					value: 0
				}
			},
			transparent: true,
			fragmentShader,
			vertexShader
		});

		this.scale.set(this.bounds.width, this.bounds.height, this.bounds.width / 2);
		this.position.y = window.pageYOffset || document.documentElement.scrollTop + this.sizes.screen.height / 2 - this.bounds.top - this.bounds.height / 2;
		this.position.x = 0 - this.sizes.screen.width / 2 + this.bounds.left + this.bounds.width / 2;
	}

	update(scroll) {
		this.scroll = scroll;

		if (this.scroll + this.sizes.screen.height >= this.bounds.top
			&& this.scroll <= this.bounds.top + this.bounds.height) {

			if (!this.visible) this.visible = true;

			this.position.y = scroll + this.sizes.screen.height / 2 - this.bounds.top - this.bounds.height / 2;
			this.position.x = 0 - this.sizes.screen.width / 2 + this.bounds.left + this.bounds.width / 2;

			const offset = (this.sizes.screen.height - this.bounds.height) * 0.5;
			this.material.uniforms.parallax.value = map(
				this.scroll + offset,
				this.bounds.top,
				this.bounds.height + this.bounds.top,
				0, 1
			);
		} else if (this.visible) this.visible = false;
	}

	resize({ sizes, bounds }) {
		this.sizes = sizes;
		this.bounds = bounds;

		this.scale.set(this.bounds.width, this.bounds.height, this.bounds.width / 2);
		this.position.y = this.scroll + this.sizes.screen.height / 2 - this.bounds.top - this.bounds.height / 2;
		this.position.x = 0 - this.sizes.screen.width / 2 + this.bounds.left + this.bounds.width / 2;
	}

	/**
	 * Animations.
	 */

	show() {
		this.isAnimating = true;

		this.timelineIn = gsap.timeline();
		this.timelineIn
			.to(this.material.uniforms.alpha, {
				ease: 'power4.inOut',
				duration: 0.5,
				value: 1
			}, 'start')
			.call(() => {
				this.isAnimating = false;
			});
	}

	hide(onComplete) {
		return new Promise(resolve => {
			this.isAnimating = true;

			this.timelineOut = gsap.timeline({
				onComplete
			});

			this.timelineOut
				.to(this.material.uniforms.alpha, {
					ease: 'power4.inOut',
					duration: 0.5,
					value: 0
				}, 'start')
				.call(() => {
					this.isAnimating = false;
					resolve();
				});
		});
	}

	/**
	 * Destroy.
	 */

	destroyGeometry() {
		if (this.geometry) {
			this.geometry.dispose();
		}
	}

	destroyMaterial() {
		if (this.material) {
			this.material.dispose();
		}
	}

	destroy() {
		this.destroyGeometry();
		this.destroyMaterial();
		this.removeEventListeners();
	}

}
